import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { Button, Row, Col, Card, CardBody } from 'reactstrap';
import { connect } from 'react-redux';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { showNotification } from 'src/shared/components/Notification';
import renderSelectField from 'src/shared/components/form/Select';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SaveIcon from '@mui/icons-material/Save';

const required = value => value ? undefined : 'Required';

const renderCompactField = ({ input, placeholder, type, meta: { touched, error, warning }, disabled, icon: Icon }) => (
    <div>
        <div className={`input-group input-group-sm ${touched && error ? 'has-validation' : ''}`}>
            {Icon && (
                <span className="input-group-text bg-light border-end-0 text-muted" style={{ padding: '0.25rem 0.5rem' }}>
                    <Icon style={{ fontSize: '1rem' }} />
                </span>
            )}
            <input
                {...input}
                className={`form-control form-control-sm ${touched && error ? 'is-invalid' : ''} ${Icon ? 'border-start-0' : ''}`}
                placeholder={placeholder}
                type={type}
                disabled={disabled}
                style={{ fontSize: '0.85rem' }}
            />
            {touched && ((error && <div className="invalid-feedback">{error}</div>) || (warning && <div className="text-warning small">{warning}</div>))}
        </div>
    </div>
);

const DriverVehicleMappingUpdate = ({ handleSubmit, driverData }) => {
    const [isVehicleList, setVehicleList] = useState([]);

    useEffect(() => {
        getAllUnAssignedVehicleDetails();
    }, []);

    const getAllUnAssignedVehicleDetails = async () => {
        try {
            const response = await PndRestApi.GetAllUnAssignedVehicleDetails();
            if (response.status === 200 && response.data.status === 200) {
                let vehcileList = [];
                response.data.data.forEach(item => {
                    vehcileList.push({ "label": item.plateNo + " (" + item?.vehicleModelInfo?.modelName + ")", "value": item.vehicleId });
                });
                setVehicleList(vehcileList);
            } else if (response.status === 200 && response.data.status === 204) {
                showNotification(
                    "theme-light",
                    "ltr",
                    `SuccessCode: ${response.data.status}`,
                    `Message:${response.data?.message}`,
                );
            } else {
                showNotification(
                    "theme-light",
                    "ltr",
                    `SuccessCode: ${response.data.status}`,
                    `Message:${response?.data?.message ? response?.data?.message : ERROR_MESSAGES.tryCatchError}`,
                );
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            showNotification(
                "theme-light",
                "ltr",
                `SuccessCode: 500`,
                ERROR_MESSAGES.tryCatchError,
            );
        }
    };

    return (
        <form onSubmit={handleSubmit} className="p-2 bg-light">
            {/* Mapping Details Section */}
            <Card className="border-0 shadow-sm mb-3">
                <CardBody className="p-3">
                    <div className="d-flex align-items-center mb-3 text-primary border-bottom pb-1" style={{ color: '#6d28d9' }}>
                        <DirectionsCarIcon style={{ fontSize: '1.2rem', color: '#6d28d9' }} className="me-2" />
                        <h6 className="m-0 fw-bold small text-uppercase" style={{ color: '#6d28d9' }}>Mapping Details</h6>
                    </div>
                    <Row className="g-3">
                        <Col md={6}>
                            <div className="mb-2">
                                <label className="form-label fw-bold text-muted small mb-1" style={{ fontSize: '0.75rem' }}>Unique ID / Username</label>
                                <Field
                                    validate={required}
                                    component={renderCompactField}
                                    name="userName"
                                    type="text"
                                    placeholder="Username"
                                    disabled={true}
                                    icon={AccountCircleIcon}
                                />
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-2">
                                <label className="form-label fw-bold text-muted small mb-1" style={{ fontSize: '0.75rem' }}>Driver Full Name</label>
                                <Field
                                    validate={required}
                                    component={renderCompactField}
                                    name="driverName"
                                    type="text"
                                    placeholder="Full Name"
                                    disabled={true}
                                    icon={PersonIcon}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row className="g-3 mt-1">
                        <Col md={12}>
                            <div className="mb-2">
                                <label className="form-label fw-bold text-muted small mb-1" style={{ fontSize: '0.75rem' }}>Vehicle <span className='text-danger'>*</span></label>
                                {driverData && driverData?.vehicleId ? (
                                    <Field
                                        validate={required}
                                        component={renderCompactField}
                                        name="vehicleId"
                                        type="text"
                                        placeholder="Vehicle Plate No"
                                        disabled={true}
                                        icon={DirectionsCarIcon}
                                    />
                                ) : (
                                    <Field
                                        validate={[required]}
                                        name="vehicleId"
                                        component={renderSelectField}
                                        options={isVehicleList}
                                        placeholder="Select Vehicle..."
                                    />
                                )}
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Actions Bar */}
            <div className="pt-2 border-top">
                <Button
                    color="primary"
                    type="submit"
                    size="sm"
                    className="w-100 py-2 rounded-pill shadow-sm d-flex align-items-center justify-content-center"
                    style={{ background: 'linear-gradient(90deg, #8b5cf6 0%, #6d28d9 100%)', border: 'none', color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}
                >
                    <SaveIcon style={{ fontSize: '1rem' }} className="me-1" />
                    Assign
                </Button>
            </div>
        </form>
    );
};

DriverVehicleMappingUpdate.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
};

const mapStateToProps = (state, data) => {
    if (data && data.driverData) {
        return {
            initialValues: {
                driverName: data?.driverData.driverName,
                userName: data?.driverData.userName,
                vehicleId: data?.driverData?.vehiclePlateNo,
                driverId: data.driverData.driverId,
            }
        }
    }
    return {};
}

export default connect(mapStateToProps)(reduxForm({ form: 'DriverVehicleMappingUpdateForm', enableReinitialize: true })(DriverVehicleMappingUpdate));